import { useEffect, useRef, useState } from 'react';

import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { toSentenceCase } from '@npi/buddy-utils';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useMutation, useQuery } from '@redwoodjs/web';

import { InputBaseField } from 'src/components/common/form/FormInput';
import { SelectBaseField } from 'src/components/common/form/FormSelect';
import TextEditor from 'src/components/common/TextEditor';
import Loader from 'src/components/Loader/Loader';
import MainCard from 'src/components/MainCard/MainCard';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { GET_MCC, GET_MCCS } from 'src/graphql/queries/mccs.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';

import { UPDATE_MCC_MUTATION } from '../../graphql/mutations/mccs.mutation';
import { RISK } from '../AddMccPage/AddMccPage';

type EditMccProps = {
    id: string;
};
interface UpdateMccInput {
    name: string;
    risk: RISK;
    formattedDescription?:
        | {
              type: string;
              text: string;
          }[]
        | string;
    formattedDocuments?:
        | {
              type: string;
              text: string;
          }[]
        | string;
}

const EditMccPage = ({ id }: EditMccProps) => {
    const timeout = useRef(null);
    const [descriptionTextValues, setDescriptionTextValues] = useState();
    const [descriptionErrorMessage, setDescriptionErrorMessage] = useState('');
    const [documentsTextValues, setDocumentsTextValues] = useState();
    const [documentsErrorMessage, setDocumentsErrorMessage] = useState('');

    const [updateMcc, { loading }] = useMutation(UPDATE_MCC_MUTATION, {
        onCompleted: (data) => {
            showSuccessToast(`${data.updateMcc.code} category code updated`);
            timeout.current = setTimeout(() => {
                return navigate('/mccs');
            }, 1000);
        },
        refetchQueries: [
            {
                query: GET_MCCS,
                variables: { input: {}, page: { page: 0, pageSize: 5 } },
            },
        ],
        onError: (error) => {
            showErrorToast(error.message);
        },
    });

    useEffect(() => {
        return () => {
            return clearTimeout(timeout.current);
        };
    }, [timeout]);

    const mccQuery = useQuery(GET_MCC, { variables: { id } });
    if (mccQuery.loading) return <Loader />;
    const {
        data: { mcc },
    } = mccQuery;

    const onSubmit = (input: UpdateMccInput) => {
        const data = { ...input };
        if (!data.risk) data.risk = mcc.risk;
        data.formattedDescription = descriptionTextValues;
        data.formattedDocuments = documentsTextValues;
        if (!data.formattedDescription) {
            setDescriptionErrorMessage('MCC Description is required');
            return;
        }
        if (!data.formattedDocuments) {
            setDocumentsErrorMessage('Supporting Documents is required');
            return;
        }
        updateMcc({ variables: { id, input: data } });
    };

    const initialValues = {
        name: mcc?.name,
        risk: mcc?.risk,
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().required('Name is required'),
    });

    const riskMenuItems = [
        { key: 'LOW', value: 'Low' },
        { key: 'MEDIUM', value: 'Medium' },
        { key: 'HIGH', value: 'High' },
        { key: 'RESTRICTED', value: 'Restricted' },
        { key: 'PROHIBITED', value: 'Prohibited' },
    ];

    return (
        <PageContent heading="Update Merchant Category Code">
            <MetaTags title="Edit Mcc" description="Edit Mcc" />
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
            >
                {(props: FormikProps<UpdateMccInput>) => {
                    return (
                        <Form>
                            <Stack spacing={5}>
                                <MainCard boxShadow>
                                    <h2 className="font-normal mb-0 mt-10">MCC Details</h2>
                                    <p>Enter Merchant Category Code details</p>
                                    <Box className="mt-20">
                                        <Stack spacing={4} sx={{ marginBottom: '40px' }}>
                                            <Box>
                                                <Grid container spacing={5}>
                                                    <Grid item xs={12} md={6}>
                                                        <label className="mb-5 font-bold">
                                                            Merchant Category Code
                                                        </label>
                                                        <Typography
                                                            variant="h3"
                                                            className="mt-20 font-normal black-200"
                                                        >
                                                            {mcc?.code}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        {mcc?.risk}
                                                        <SelectBaseField
                                                            label="Risk Classification"
                                                            name="risk"
                                                            inputLabel={toSentenceCase(mcc?.risk)}
                                                            initialValue={mcc?.risk}
                                                            menuItems={riskMenuItems}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                            <Box>
                                                <Grid
                                                    container
                                                    spacing={3}
                                                    sx={{ height: '110px' }}
                                                >
                                                    <Grid item xs={12} md={6}>
                                                        <InputBaseField
                                                            label="MCC Friendly Name"
                                                            name="name"
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                            <Box>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12} md={6}>
                                                        <TextEditor
                                                            label="MCC Description"
                                                            setTextValues={setDescriptionTextValues}
                                                            errorMessage={descriptionErrorMessage}
                                                            initialValue={
                                                                mcc?.formattedDescription
                                                                    ? mcc?.formattedDescription
                                                                    : [
                                                                          {
                                                                              text: mcc?.description,
                                                                              type: '',
                                                                          },
                                                                      ]
                                                            }
                                                            placeholder="Enter a description..."
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <TextEditor
                                                            label="Supporting Documents"
                                                            setTextValues={setDocumentsTextValues}
                                                            errorMessage={documentsErrorMessage}
                                                            initialValue={
                                                                mcc?.formattedDocuments
                                                                    ? mcc?.formattedDocuments
                                                                    : [
                                                                          {
                                                                              text: mcc?.documents,
                                                                              type: '',
                                                                          },
                                                                      ]
                                                            }
                                                            placeholder="Enter all documents that will be uploaded alongside this application..."
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Stack>
                                    </Box>
                                </MainCard>
                                <Box>
                                    <div className="flex space-end">
                                        <Button
                                            onClick={() => {
                                                return navigate('/mccs');
                                            }}
                                            className="reset-btn btn-medium mr-20"
                                            variant="contained"
                                            size="medium"
                                            disableElevation
                                        >
                                            Cancel
                                        </Button>
                                        {loading ? (
                                            <LoadingButton
                                                className="btn-medium"
                                                style={{
                                                    backgroundColor: '#F18F34',
                                                    color: '#fff',
                                                    padding: '5px 30px 5px 10px',
                                                }}
                                                loading
                                                loadingPosition="start"
                                                startIcon={<SaveIcon />}
                                                variant="contained"
                                            >
                                                Save
                                            </LoadingButton>
                                        ) : (
                                            <Box className="btn-wrapper">
                                                <Button
                                                    className={`btn-primary btn-medium`}
                                                    disabled={loading}
                                                    type="submit"
                                                    variant="contained"
                                                    size="medium"
                                                    disableElevation
                                                >
                                                    Save
                                                </Button>
                                            </Box>
                                        )}
                                    </div>
                                </Box>
                            </Stack>
                        </Form>
                    );
                }}
            </Formik>
        </PageContent>
    );
};

export default EditMccPage;
